import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import austinDowntownImg from "@/assets/audi-downtown-night.png";
import austinDowntownVideo from "@/assets/audi-downtown-night.mp4.asset.json";

const SanAntonioToAustin = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/san-antonio-to-austin#service",
    "name": "Car Service San Antonio to Austin",
    "alternateName": "AUS Airport Transfers",
    "serviceType": "Intercity Car Service",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "San Antonio",
        "containedInPlace": {
          "@type": "State",
          "name": "Texas"
        }
      },
      {
        "@type": "City",
        "name": "Austin",
        "containedInPlace": {
          "@type": "State",
          "name": "Texas"
        }
      }
    ],
    "description": "Private car service between San Antonio and Austin, including Austin-Bergstrom (AUS) airport transfers. Flat rates, luxury SUV, 24/7. Call 210-296-7409.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/san-antonio-to-austin",
      "priceCurrency": "USD",
      "lowPrice": "474",
      "highPrice": "474",
      "offerCount": "1",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$474"
  };

  return (
    <>
      <SEO
        title="Car Service San Antonio to Austin | AUS Airport Transfers | Rick's"
        description="Private car service between San Antonio and Austin, including Austin-Bergstrom (AUS) airport transfers. Flat rates, luxury SUV, 24/7. Call 210-296-7409."
        canonicalPath="/san-antonio-to-austin"
        schema={serviceSchema}
        includeBreadcrumbs={false}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb items={[{ name: "San Antonio to Austin", url: "/san-antonio-to-austin" }]} />
          {/* Hero Section */}
          <section className="relative min-h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                src={austinDowntownVideo.url}
                poster={austinDowntownImg}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                disableRemotePlayback
                aria-label="2025 Audi Q7 luxury SUV on the San Antonio to Austin corridor"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                Private Car Service Between San Antonio and Austin
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Direct, private transportation between San Antonio and Austin — a productive, comfortable alternative to driving I-35 yourself.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Online Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    The San Antonio–Austin corridor is one of the busiest stretches in Texas, and I-35 doesn't care about your meeting time or your flight departure. Rick's Black Car Service provides direct, private transportation between San Antonio and Austin — a productive, comfortable alternative to driving yourself, gambling on a rideshare, or squeezing into a shuttle.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Austin-Bergstrom (AUS) Airport Transfers
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Flying out of Austin instead of San Antonio? It happens all the time — better routes, better fares, or a connecting itinerary. We provide door-to-door transfers from anywhere in the San Antonio area straight to Austin-Bergstrom International Airport, with the same <Link to="/airport-transportation" className="text-primary hover:underline">flight-tracking service we offer at SAT</Link>. We also pick up arriving passengers at AUS and bring them home to San Antonio, Boerne, New Braunfels, and beyond — even for late-night arrivals, because we run 24/7.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Business Travel That Doesn't Waste Your Time
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    For executives, the 80-mile drive to Austin is 90 minutes of potential work time lost behind the wheel. In the back of our 2025 Audi Q7, it's a quiet mobile office: take calls, prep for the meeting, answer email, or simply decompress. We handle downtown Austin drop-offs, The Domain, the Capitol complex, tech campuses, and conference venues, and we'll wait or return for your trip home. Corporate accounts get priority booking and simplified billing.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Events, Concerts, and Game Days
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Headed to a concert at Moody Center, a festival weekend, an F1 race at Circuit of the Americas, or a Longhorns game? Skip the parking nightmare and surge pricing. We drop you at the gate and pick you up when it ends — no matter how late.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Flat-Rate Intercity Pricing
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    San Antonio–Austin trips are quoted as a flat rate before you book. No meters, no surge, no per-mile surprises when traffic stacks up near San Marcos. One price, confirmed in advance, with a professional chauffeur and a guaranteed vehicle.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Also Serving New Braunfels and San Marcos
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Along the corridor? We provide pickups and drop-offs in New Braunfels, Gruene, San Marcos, and Kyle — convenient for outlet shopping trips, river weekends, and Texas State events.
                  </p>
                </div>

                {/* Bottom CTA */}
                <div className="text-center py-12 border-t border-border/50">
                  <p className="text-xl md:text-2xl text-foreground font-semibold mb-6">
                    Get a flat-rate quote for your San Antonio–Austin trip — call or text 210-296-7409, available 24/7.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        Call 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Reserve Online
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default SanAntonioToAustin;
